import {
  TextField,
  Box,
  CircularProgress,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableSortLabel,
  InputAdornment,
  Collapse,
  IconButton,
  Grid,
  TablePagination,
  Typography,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Input,
  Card,
} from '@mui/material'
import { formatQuantity } from 'shared/numberFormatter'

import React, { useMemo } from 'react'
import debounce from 'lodash/debounce'
import { IPackagingOrder, PackagingOrder } from 'interfaces/IPackaging'
import makeStyles from '@mui/styles/makeStyles'
import API from '@aws-amplify/api'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import SearchIcon from '@mui/icons-material/Search'
import { Alert } from '@mui/material'
import { BrowserView, MobileView } from 'react-device-detect'

const useStyles = makeStyles((theme) => ({
  tinyCell: {
    width: '1%',
  },
  smallCell: {
    width: '5%',
  },
  mediumCell: {
    width: '10%',
  },
  tableSubRow: {
    backgroundColor: '#f7fbff',
  },
  selectedCard: {
    backgroundColor: 'rgba(255, 159, 165, 0.08)',
  },
}))

const ROWS_PER_PAGE = 25
let originalData: Partial<IPackagingOrder>[] | undefined = []

export default function PackagingFinder(props: {
  selectedPackagings?: Partial<PackagingOrder>[]
  getSelectedPackagings?: (packagings: Partial<PackagingOrder>[]) => void
  getAllPackagings?: (packagings: Partial<PackagingOrder>[]) => void
  packagingData?: Partial<PackagingOrder>[]
  editableQty?: boolean
  showQty?: boolean
  hidePrice?: boolean
  showInventory?: boolean
  showDiscount?: boolean
  editableDiscount?: boolean
  editablePrice?: boolean
  isInhouse?: boolean
  showQtyEmphasis?: boolean
  hideNoQtyPackagings?: boolean
  hidePackagingRank?: boolean
  showSelectedPackagings?: boolean
  hideSearch?: boolean
  updateSelectedPackagings?: boolean
  setUpdateSelectedPackagings?: (shouldUpdate: boolean) => void
  onValidationChange?: (hasErrors: boolean) => void
  validateStock?: boolean
}) {
  const { updateSelectedPackagings, setUpdateSelectedPackagings } = props
  const [sortingOrder, setSortingOrder] = React.useState<'asc' | 'desc'>('asc')
  const [orderBy, setOrderBy] =
    React.useState<keyof IPackagingOrder>('packagingPartId')
  const [packagings, setPackagings] = React.useState<
    Partial<IPackagingOrder>[]
  >(props.packagingData || [])
  const [searchTerm, setSearchTerm] = React.useState('')
  const [isLoading, setIsLoading] = React.useState<any>(
    props.packagingData ? false : true
  )
  const [selectedPackagings, setSelectedPackagings] = React.useState<
    Partial<PackagingOrder>[]
  >(props.selectedPackagings || [])

  const [expandedRows, setExpandedRows] = React.useState<{
    [key: string]: boolean
  }>(
    packagings.reduce<{
      [key: string]: boolean
    }>((acc, packaging) => {
      acc[packaging.packagingPartId || ''] = true
      return acc
    }, {})
  )
  const [page, setPage] = React.useState(0)
  const [totalCount, setTotalCount] = React.useState(0)

  const classes = useStyles()

  React.useEffect(() => {
    if (updateSelectedPackagings && setUpdateSelectedPackagings) {
      setSelectedPackagings(props.selectedPackagings || [])
      setUpdateSelectedPackagings(false)
    }
  }, [
    updateSelectedPackagings,
    setUpdateSelectedPackagings,
    props.selectedPackagings,
  ])

  const loadPackaging = (
    searchTermInput?: string,
    pageInput?: number,
    pageSizeInput?: number
  ): void => {
    setIsLoading(true)
    const currentSearchTerm =
      searchTermInput !== undefined ? searchTermInput : searchTerm
    const currentPage = pageInput !== undefined ? pageInput : page
    const currentPageSize =
      pageSizeInput !== undefined ? pageSizeInput : ROWS_PER_PAGE

    const param = {
      queryStringParameters: {
        packagingStatus: 'Active',
        search: currentSearchTerm || undefined,
        page: currentPage + 1,
        pageSize: currentPageSize,
      },
    }
    API.get('products', `/packaging/getPackagingPart`, param)
      .then((response: any) => {
        const responseData = Array.isArray(response)
          ? response
          : response.data || []
        const count = response.count || responseData.length

        setPackagings(
          responseData.map((packaging: PackagingOrder, index: number) => ({
            ...packaging,
            rank: currentPage * currentPageSize + index + 1,
          }))
        )
        setTotalCount(count)
      })
      .catch((error) => {
        console.error(error)
        console.error(error.response)
      })
      .finally(() => {
        setIsLoading(false)
      })
  }

  const debouncedLoadPackaging = useMemo(
    () =>
      debounce((searchTerm: string) => {
        setPage(0)
        loadPackaging(searchTerm, 0)
      }, 500),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )

  React.useEffect(() => {
    if (!props.packagingData || !props.packagingData.length)
      loadPackaging(searchTerm, page, ROWS_PER_PAGE)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  React.useEffect(() => {
    if (props.getSelectedPackagings)
      props.getSelectedPackagings(selectedPackagings)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPackagings])
  React.useEffect(() => {
    if (props.getAllPackagings) props.getAllPackagings(packagings)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [packagings])
  React.useEffect(() => {
    originalData = props.packagingData
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  React.useEffect(() => {
    if (props.onValidationChange && props.showInventory) {
      const validateStock = props.validateStock !== false
      const hasErrors =
        validateStock &&
        packagings.some(
          (packaging) =>
            packaging.qty !== null &&
            packaging.qty !== undefined &&
            Number(packaging.qty) > Number(packaging.stock || 0)
        )
      props.onValidationChange(!!hasErrors)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [packagings, props.validateStock])

  const getFilteredPackagings = () => {
    return packagings
  }

  function stableSort(
    array: Partial<PackagingOrder>[],
    comparator: (a: PackagingOrder, b: PackagingOrder) => number
  ) {
    const stabilizedThis = array.map(
      (el, index) => [el, index] as [PackagingOrder, number]
    )
    stabilizedThis.sort((a, b) => {
      const order = comparator(a[0], b[0])
      if (order !== 0) return order
      return a[1] - b[1]
    })
    return stabilizedThis.map((el) => el[0])
  }

  function getComparator<Key extends keyof PackagingOrder>(
    order: 'asc' | 'desc',
    orderBy: Key
  ): (a: Partial<PackagingOrder>, b: Partial<PackagingOrder>) => number {
    return order === 'desc'
      ? (a, b) => descendingComparator(a, b, orderBy)
      : (a, b) => -descendingComparator(a, b, orderBy)
  }

  function descendingComparator<T>(
    a: Partial<T>,
    b: Partial<T>,
    orderBy: keyof T
  ) {
    if ((b[orderBy] || 0) < (a[orderBy] || 0)) {
      return -1
    }
    if ((b[orderBy] || 0) > (a[orderBy] || 0)) {
      return 1
    }
    return 0
  }

  const headCells = () => {
    let headers = []
    if (props.showQty) {
      headers.push({
        id: 'qty',
        numeric: false,
        disablePadding: false,
        label: 'Qty',
        width: 'small',
      })
    }

    if (props.showInventory) {
      headers.push({
        id: 'stock',
        numeric: false,
        disablePadding: false,
        label: 'Stok',
        width: 'small',
      })
    }

    headers.push(
      ...[
        {
          id: 'partName',
          numeric: false,
          disablePadding: false,
          label: 'Nama',
          width: 'medium',
        },
        {
          id: 'packagingPartId',
          numeric: false,
          disablePadding: false,
          label: 'Kode Kemasan',
          width: 'medium',
        },
      ]
    )

    // Price column intentionally removed for this form

    if (props.showDiscount) {
      headers.push({
        id: 'discount',
        numeric: false,
        disablePadding: false,
        label: 'Diskon',
        width: 'small',
      })
    }

    return headers
  }

  const selectedHeadCells = () => {
    if (!props.hidePackagingRank) {
      return [
        ...headCells().slice(0, 2),
        {
          id: 'rank',
          numeric: false,
          disablePadding: false,
          label: 'Urutan',
          width: 'tiny',
        },
        ...headCells().slice(2),
      ]
    }
    return headCells()
  }

  const createSortHandler =
    (property: keyof PackagingOrder) => (event: React.MouseEvent<unknown>) => {
      const isAsc = orderBy === property && sortingOrder === 'asc'
      setSortingOrder(isAsc ? 'desc' : 'asc')
      setOrderBy(property)
    }

  const handleDiscountChange = (
    packagingId: string,
    discountInput?: string,
    discountType?: string
  ) => {
    const matchingPackaging =
      packagings.find(
        (packaging) => packaging.packagingPartId === packagingId
      ) || new PackagingOrder()

    let discount = discountInput ? Number(discountInput) : 0

    if (discount < 0) {
      return
    }

    let finalDiscount = 0
    if (matchingPackaging.discountType === 'percent') {
      discount = Math.min(discount, 100)
      finalDiscount =
        (Number(matchingPackaging.unitPrice || 0) *
          Number(matchingPackaging.qty || 0) *
          discount) /
        100
    } else if (matchingPackaging.discountType === 'grosir') {
      discount = Math.min(discount, Number(matchingPackaging.unitPrice || 0))
      finalDiscount =
        (Number(matchingPackaging.unitPrice || 0) - discount) *
        Number(matchingPackaging.qty || 0)
    } else if (matchingPackaging.discountType === 'pcs') {
      discount = Math.min(discount, Number(matchingPackaging.qty))
      finalDiscount = Number(matchingPackaging.unitPrice || 0) * discount
    } else {
      finalDiscount = discount
    }

    const finalPackaging = {
      ...matchingPackaging,
      discount: finalDiscount,
      displayDiscount: discount,
      ...(discountType != null && { discountType }),
    }

    setPackagings((prevState) =>
      prevState.map((prevPackaging) => {
        if (prevPackaging.packagingPartId === packagingId) {
          return finalPackaging
        }
        return prevPackaging
      })
    )

    setSelectedPackagings((prevState) =>
      prevState.map((prevPackaging) => {
        if (prevPackaging.packagingPartId === packagingId) {
          return finalPackaging
        }
        return prevPackaging
      })
    )
  }

  const handleQtyChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    packagingId: string
  ) => {
    const rawValue = e.target.value
    let qty: number | null = rawValue === '' ? null : Number(rawValue)
    if (qty !== null && (Number.isNaN(qty) || qty < 0)) {
      qty = null
    }

    // make sure we can't go above the ordered amount
    const originalPackaging = originalData?.find(
      (packaging) => packaging.packagingPartId === packagingId
    )

    if (
      !props.isInhouse &&
      props.validateStock !== false &&
      originalPackaging &&
      qty !== null &&
      Number(qty) > (originalPackaging.stock || 0)
    ) {
      setPackagings((prevState) =>
        prevState.map((prevPackaging) =>
          prevPackaging.packagingPartId === packagingId
            ? {
                ...prevPackaging,
                qty: originalPackaging.qty || (null as any),
                discount: 0,
                displayDiscount: 0,
              }
            : prevPackaging
        )
      )
    } else {
      setPackagings((prevState) =>
        prevState.map((prevPackaging) =>
          prevPackaging.packagingPartId === packagingId
            ? {
                ...prevPackaging,
                qty: rawValue as any,
                discount: 0,
                displayDiscount: 0,
              }
            : prevPackaging
        )
      )
    }

    const isSelected = isPackagingSelected(packagingId)
    // remove packaging from list of selected packagings if qty is 0 or null
    if (!qty) {
      setSelectedPackagings((prevState) => {
        return prevState.filter(
          (packaging) => packaging.packagingPartId !== packagingId
        )
      })
    } else {
      setSelectedPackagings((prevState) => {
        const notSelectedAndNotEmpty = Number(qty) > 0 && !isSelected
        if (notSelectedAndNotEmpty) {
          const matchingPackaging =
            packagings.find(
              (packaging) => packaging.packagingPartId === packagingId
            ) || new PackagingOrder()
          return [...prevState, { ...matchingPackaging, qty }].map(
            (packaging, idx) => ({ ...packaging, rank: idx + 1 })
          )
        } else {
          return selectedPackagings.map((prevPackaging) =>
            prevPackaging.packagingPartId === packagingId
              ? {
                  ...prevPackaging,
                  qty,
                  discount: 0,
                }
              : prevPackaging
          )
        }
      })
    }
  }

  const isPackagingSelected = (packagingId: string) => {
    return !!selectedPackagings.find(
      (selectedPackaging) => selectedPackaging.packagingPartId === packagingId
    )
  }

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage)
    loadPackaging(searchTerm, newPage)
  }

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setSearchTerm(value)
    debouncedLoadPackaging(value)
  }

  const handleDiscountTypeChange = (
    packagingId: string,
    discountType: string
  ) => {
    handleDiscountChange(packagingId, undefined, discountType)
  }

  const getPackagingsToShow = () => {
    let data = stableSort(
      getFilteredPackagings(),
      getComparator(sortingOrder, orderBy)
    )
    if (props.hideNoQtyPackagings) {
      data = data.filter((packaging) => packaging.qty)
    }

    return data
  }

  const getSelectedPackagingsToShow = () => {
    let data = stableSort(selectedPackagings, getComparator('asc', 'rank'))
    return data
  }

  const handleRankChange = (packagingPartId: string, direction: string) => {
    const matchingPackaging = selectedPackagings.find(
      (packaging) => packaging.packagingPartId === packagingPartId
    )

    if (matchingPackaging != null) {
      const filteredPackaging = selectedPackagings.filter(
        (packaging) => packaging.packagingPartId !== packagingPartId
      )

      const newRank =
        direction === 'up'
          ? (matchingPackaging.rank || 0) + 1
          : Math.max((matchingPackaging.rank || 0) - 1, 1)

      let rerankedPackaging = [
        ...filteredPackaging.slice(0, newRank - 1),
        matchingPackaging,
        ...filteredPackaging.slice(newRank - 1),
      ]

      rerankedPackaging = rerankedPackaging.map((packaging, index) => {
        return {
          ...packaging,
          rank: index + 1,
        }
      })
      setSelectedPackagings(rerankedPackaging)
    }
  }

  const getTotalCount = () => {
    return totalCount
  }

  const shouldShowExpand = () => {
    return props.editableDiscount || props.editableQty
  }

  return (
    <React.Fragment>
      {props.showSelectedPackagings && selectedPackagings.length > 0 && (
        <Box mb={4} ml={2} mt={2}>
          <Typography variant="h6">Kemasan yang sudah terpilih</Typography>
          <BrowserView>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    {selectedHeadCells().map((headCell) => (
                      <TableCell
                        key={headCell.id + 'header1'}
                        align={headCell.numeric ? 'right' : 'left'}
                        padding={headCell.disablePadding ? 'none' : 'normal'}
                        sortDirection={
                          orderBy === headCell.id ? sortingOrder : false
                        }
                        // @ts-ignore
                        className={classes[`${headCell.width}Cell`]}
                      >
                        {headCell.label}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {getSelectedPackagingsToShow().map((packaging) => (
                    <TableRow>
                      {props.showQty && <TableCell>{formatQuantity(packaging.qty)}</TableCell>}
                      {props.showInventory && (
                        <TableCell>{formatQuantity(packaging.stock)}</TableCell>
                      )}
                      <TableCell>{packaging.rank}</TableCell>
                      <TableCell>{packaging.partName}</TableCell>
                      <TableCell>{packaging.packagingPartId}</TableCell>
                      {/* weight and price columns removed for this form */}
                      {props.showDiscount && (
                        <TableCell>
                          {`Rp.${new Intl.NumberFormat('id-ID').format(
                            Number(packaging.discount || 0)
                          )}`}
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </BrowserView>
          <MobileView>
            {getSelectedPackagingsToShow().map((packaging) => (
              <Box pb={2} pt={2}>
                <Card>
                  <TableContainer>
                    <Table size="small">
                      <TableBody>
                        <React.Fragment>
                          <TableRow>
                            <TableCell>
                              <Box fontWeight="bold" display="inline">
                                Kode Kemasan
                              </Box>
                            </TableCell>
                            <TableCell>{packaging.packagingPartId}</TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell>
                              <Box fontWeight="bold" display="inline">
                                Nama Kemasan
                              </Box>
                            </TableCell>
                            <TableCell>{packaging.partName}</TableCell>
                          </TableRow>
                          {/* Qty row */}
                          {props.showQty && (
                            <TableRow>
                              <TableCell>
                                <Box fontWeight="bold" display="inline">
                                  Qty
                                </Box>
                              </TableCell>
                              <TableCell>
                                {props.editableQty ? (
                                  <TextField
                                    label="Qty"
                                    type="number"
                                    value={packaging.qty}
                                    onChange={(e) =>
                                      handleQtyChange(
                                        e,
                                        packaging.packagingPartId!
                                      )
                                    }
                                    inputProps={{
                                      min: 0,
                                      step: '0.000001',
                                    }}
                                    error={
                                      props.showInventory &&
                                      props.validateStock !== false &&
                                      packaging.qty !== null &&
                                      packaging.qty !== undefined &&
                                      Number(packaging.qty) >
                                        Number(packaging.stock || 0)
                                    }
                                    helperText={
                                      props.showInventory &&
                                      props.validateStock !== false &&
                                      packaging.qty !== null &&
                                      packaging.qty !== undefined &&
                                      Number(packaging.qty) >
                                        Number(packaging.stock || 0)
                                          ? `Melebihi stok (${formatQuantity(
                                              packaging.stock
                                            )})`
                                        : ''
                                    }
                                  />
                                ) : (
                                  formatQuantity(packaging.qty)
                                )}
                              </TableCell>
                            </TableRow>
                          )}
                          {props.showInventory && (
                            <TableRow>
                              <TableCell>
                                <Box fontWeight="bold" display="inline">
                                  Stok
                                </Box>
                              </TableCell>
                              <TableCell>{formatQuantity(packaging.stock ?? '-')}</TableCell>
                            </TableRow>
                          )}
                          {/* price removed from selected mobile view */}
                          {props.showDiscount && (
                            <React.Fragment>
                              <TableRow>
                                <TableCell>
                                  <Box fontWeight="bold" display="inline">
                                    Diskon
                                  </Box>
                                </TableCell>
                                <TableCell>
                                  {`Rp.${new Intl.NumberFormat('id-ID').format(
                                    Number(packaging.discount || 0)
                                  )}`}
                                  {shouldShowExpand() && (
                                    <IconButton
                                      aria-label="expand row"
                                      size="small"
                                      onClick={(e) => {
                                        e.stopPropagation()
                                        setExpandedRows({
                                          ...expandedRows,
                                          [packaging.packagingPartId!]:
                                            !expandedRows[
                                              packaging.packagingPartId!
                                            ],
                                        })
                                      }}
                                    >
                                      {expandedRows[
                                        packaging.packagingPartId!
                                      ] ? (
                                        <KeyboardArrowUpIcon />
                                      ) : (
                                        <KeyboardArrowDownIcon />
                                      )}
                                    </IconButton>
                                  )}
                                </TableCell>
                              </TableRow>
                              {shouldShowExpand() &&
                                expandedRows[packaging.packagingPartId!] && (
                                  <TableRow
                                    className={classes.tableSubRow}
                                    hover
                                  >
                                    <TableCell colSpan={12}>
                                      <Collapse
                                        in={
                                          expandedRows[
                                            packaging.packagingPartId!
                                          ]
                                        }
                                        timeout="auto"
                                        unmountOnExit
                                      >
                                        <Grid container alignItems="flex-end">
                                          {props.editableDiscount && (
                                            <React.Fragment>
                                              {props.editableDiscount && (
                                                <Grid item xs={4}>
                                                  <FormControl>
                                                    <InputLabel id="discount-type">
                                                      Jenis Diskon
                                                    </InputLabel>
                                                    <Select
                                                      labelId="discount-type"
                                                      id="discount-type"
                                                      value={
                                                        packaging.discountType ||
                                                        'manual'
                                                      }
                                                      onChange={(e) =>
                                                        handleDiscountTypeChange(
                                                          packaging.packagingPartId!,
                                                          String(e.target.value)
                                                        )
                                                      }
                                                    >
                                                      <MenuItem value={'pcs'}>
                                                        Pcs
                                                      </MenuItem>
                                                      <MenuItem
                                                        value={'manual'}
                                                      >
                                                        Manual
                                                      </MenuItem>
                                                      <MenuItem
                                                        value={'percent'}
                                                      >
                                                        Percent
                                                      </MenuItem>
                                                      <MenuItem
                                                        value={'grosir'}
                                                      >
                                                        Grosir
                                                      </MenuItem>
                                                    </Select>
                                                  </FormControl>
                                                </Grid>
                                              )}
                                              <Grid item xs={8}>
                                                <InputLabel htmlFor="Diskon">
                                                  Diskon
                                                </InputLabel>
                                                <Input
                                                  id="Diskon"
                                                  autoComplete="off"
                                                  type="number"
                                                  value={
                                                    packaging.displayDiscount ||
                                                    packaging.discount ||
                                                    ''
                                                  }
                                                  onChange={(
                                                    e: React.ChangeEvent<HTMLInputElement>
                                                  ) =>
                                                    handleDiscountChange(
                                                      packaging.packagingPartId!,
                                                      e.target.value.trim()
                                                    )
                                                  }
                                                  placeholder="0"
                                                  onClick={(e) =>
                                                    e.stopPropagation()
                                                  }
                                                  disabled={
                                                    !props.editableDiscount
                                                  }
                                                  startAdornment={(() => {
                                                    if (
                                                      packaging.discountType ===
                                                        'manual' ||
                                                      !packaging.discountType ||
                                                      packaging.discountType ===
                                                        'grosir'
                                                    ) {
                                                      return (
                                                        <InputAdornment position="start">
                                                          Rp.
                                                        </InputAdornment>
                                                      )
                                                    }
                                                  })()}
                                                  endAdornment={(() => {
                                                    if (
                                                      packaging.discountType ===
                                                      'percent'
                                                    ) {
                                                      return (
                                                        <InputAdornment position="end">
                                                          %
                                                        </InputAdornment>
                                                      )
                                                    } else if (
                                                      packaging.discountType ===
                                                      'pcs'
                                                    ) {
                                                      return (
                                                        <InputAdornment position="end">
                                                          pcs
                                                        </InputAdornment>
                                                      )
                                                    } else if (
                                                      packaging.discountType ===
                                                      'grosir'
                                                    ) {
                                                      return (
                                                        <InputAdornment position="end">
                                                          hrg/pc
                                                        </InputAdornment>
                                                      )
                                                    }
                                                  })()}
                                                />
                                              </Grid>
                                              {(packaging.discountType ===
                                                'pcs' ||
                                                packaging.discountType ===
                                                  'percent' ||
                                                packaging.discountType ===
                                                  'grosir') && (
                                                <Grid item xs={12}>
                                                  <Typography variant="h6">
                                                    Rp.{' '}
                                                    {(
                                                      packaging.discount || '0'
                                                    ).toLocaleString('id-ID')}
                                                  </Typography>
                                                </Grid>
                                              )}
                                              {(Number(packaging.discount) ||
                                                0) >
                                                0.3 *
                                                  Number(packaging.unitPrice) *
                                                  Number(packaging.qty) && (
                                                <Alert severity="warning">
                                                  Diskon lebih besar dari 30%
                                                </Alert>
                                              )}
                                            </React.Fragment>
                                          )}
                                        </Grid>
                                      </Collapse>
                                    </TableCell>
                                  </TableRow>
                                )}
                            </React.Fragment>
                          )}
                          {!props.hidePackagingRank && (
                            <React.Fragment>
                              <TableCell>
                                <Box fontWeight="bold" display="inline">
                                  Urutan
                                </Box>
                              </TableCell>
                              <TableCell>
                                <Grid container alignItems="center">
                                  <Grid item xs={6}>
                                    Urutan: {packaging.rank}
                                  </Grid>
                                  <Grid item xs={6}>
                                    <Grid container justifyContent="center">
                                      <Grid item xs={12}>
                                        <IconButton
                                          aria-label="up"
                                          onClick={() =>
                                            handleRankChange(
                                              packaging.packagingPartId,
                                              'down'
                                            )
                                          }
                                          style={{
                                            paddingTop: '3px',
                                            paddingBottom: '3px',
                                          }}
                                          size="large"
                                        >
                                          <KeyboardArrowUpIcon />
                                        </IconButton>{' '}
                                      </Grid>
                                      <Grid item xs={12}>
                                        <IconButton
                                          aria-label="down"
                                          onClick={() =>
                                            handleRankChange(
                                              packaging.packagingPartId,
                                              'up'
                                            )
                                          }
                                          style={{
                                            paddingTop: '3px',
                                            paddingBottom: '3px',
                                          }}
                                          size="large"
                                        >
                                          <KeyboardArrowDownIcon />
                                        </IconButton>{' '}
                                      </Grid>
                                    </Grid>
                                  </Grid>
                                </Grid>
                              </TableCell>
                            </React.Fragment>
                          )}
                        </React.Fragment>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Card>
              </Box>
            ))}
          </MobileView>
        </Box>
      )}
      {props.showSelectedPackagings && (
        <Box ml={2}>
          <Typography variant="h6">Pilih kemasan untuk dimasukkan</Typography>
        </Box>
      )}
      <BrowserView>
        <TableContainer>
          <Table>
            <TableHead>
              {!props.hideSearch && (
                <TableRow>
                  <TableCell colSpan={3}>
                    <Box ml={2}>
                      <TextField
                        fullWidth
                        placeholder="Cari Kode/Nama Kemasan..."
                        onChange={handleSearch}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <SearchIcon />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Box>
                  </TableCell>
                </TableRow>
              )}
              <TableRow>
                {headCells().map((headCell) => (
                  <TableCell
                    key={headCell.id + 'header'}
                    align={headCell.numeric ? 'right' : 'left'}
                    padding={headCell.disablePadding ? 'none' : 'normal'}
                    sortDirection={
                      orderBy === headCell.id ? sortingOrder : false
                    }
                    // @ts-ignore
                    className={classes[`${headCell.width}Cell`]}
                  >
                    <TableSortLabel
                      active={orderBy === headCell.id}
                      direction={orderBy === headCell.id ? sortingOrder : 'asc'}
                      onClick={createSortHandler(
                        headCell.id as keyof PackagingOrder
                      )}
                    >
                      {headCell.label}
                    </TableSortLabel>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            {isLoading ? (
              <TableBody>
                <TableRow>
                  <TableCell>
                    <Box p={3}>
                      <CircularProgress />
                    </Box>
                  </TableCell>
                </TableRow>
              </TableBody>
            ) : (
              <TableBody>
                {getPackagingsToShow().map((packaging) => (
                  <React.Fragment key={packaging.packagingPartId}>
                    <TableRow
                      hover
                      selected={isPackagingSelected(packaging.packagingPartId)}
                    >
                      {props.showQty && (
                        <TableCell>
                          {props.editableQty ? (
                            <TextField
                              label="Qty"
                              type="number"
                              value={packaging.qty}
                              onChange={(e) =>
                                handleQtyChange(e, packaging.packagingPartId)
                              }
                              inputProps={{
                                min: 0,
                                step: '0.000001',
                              }}
                              error={
                                props.showInventory &&
                                props.validateStock !== false &&
                                packaging.qty !== null &&
                                packaging.qty !== undefined &&
                                Number(packaging.qty) >
                                  Number(packaging.stock || 0)
                              }
                              helperText={
                                props.showInventory &&
                                props.validateStock !== false &&
                                packaging.qty !== null &&
                                packaging.qty !== undefined &&
                                Number(packaging.qty) >
                                  Number(packaging.stock || 0)
                                  ? `Melebihi stok (${formatQuantity(
                                      packaging.stock
                                    )})`
                                  : ''
                              }
                            />
                          ) : (
                            formatQuantity(packaging.qty)
                          )}
                        </TableCell>
                      )}
                      {props.showInventory && (
                        <TableCell>{formatQuantity(packaging.stock)}</TableCell>
                      )}
                      <TableCell>{packaging.partName}</TableCell>
                      <TableCell>{packaging.packagingPartId}</TableCell>
                      {/* weight and price columns removed for this form */}
                      {props.showDiscount && (
                        <TableCell>
                          {`Rp.${new Intl.NumberFormat('id-ID').format(
                            Number(packaging.discount || 0)
                          )}`}
                          {shouldShowExpand() && (
                            <IconButton
                              aria-label="expand row"
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation()
                                setExpandedRows({
                                  ...expandedRows,
                                  [packaging.packagingPartId]:
                                    !expandedRows[packaging.packagingPartId],
                                })
                              }}
                            >
                              {expandedRows[packaging.packagingPartId] ? (
                                <KeyboardArrowUpIcon />
                              ) : (
                                <KeyboardArrowDownIcon />
                              )}
                            </IconButton>
                          )}
                        </TableCell>
                      )}
                    </TableRow>
                    {shouldShowExpand() &&
                      expandedRows[packaging.packagingPartId] && (
                        <TableRow className={classes.tableSubRow} hover>
                          <TableCell colSpan={12}>
                            <Collapse
                              in={expandedRows[packaging.packagingPartId]}
                              timeout="auto"
                              unmountOnExit
                            >
                              <Grid container alignItems="flex-end">
                                {props.editableDiscount && (
                                  <React.Fragment>
                                    <Grid item xs={4} sm={5}>
                                      <Grid
                                        container
                                        alignItems="flex-end"
                                        alignContent="space-between"
                                      >
                                        {props.editableDiscount && (
                                          <Grid item>
                                            <FormControl>
                                              <InputLabel id="discount-type">
                                                Jenis Diskon
                                              </InputLabel>
                                              <Select
                                                labelId="discount-type"
                                                id="discount-type"
                                                value={
                                                  packaging.discountType ||
                                                  'manual'
                                                }
                                                onChange={(e) =>
                                                  handleDiscountTypeChange(
                                                    packaging.packagingPartId,
                                                    String(e.target.value)
                                                  )
                                                }
                                              >
                                                <MenuItem value={'pcs'}>
                                                  Pcs
                                                </MenuItem>
                                                <MenuItem value={'manual'}>
                                                  Manual
                                                </MenuItem>
                                                <MenuItem value={'percent'}>
                                                  Percent
                                                </MenuItem>
                                                <MenuItem value={'grosir'}>
                                                  Grosir
                                                </MenuItem>
                                              </Select>
                                            </FormControl>
                                          </Grid>
                                        )}
                                        <Grid item xs={5}>
                                          <InputLabel htmlFor="Diskon">
                                            Diskon
                                          </InputLabel>
                                          <Input
                                            id="Diskon"
                                            autoComplete="off"
                                            type="number"
                                            value={
                                              packaging.displayDiscount ||
                                              packaging.discount ||
                                              ''
                                            }
                                            onChange={(
                                              e: React.ChangeEvent<HTMLInputElement>
                                            ) =>
                                              handleDiscountChange(
                                                packaging.packagingPartId,
                                                e.target.value.trim()
                                              )
                                            }
                                            placeholder="0"
                                            onClick={(e) => e.stopPropagation()}
                                            disabled={!props.editableDiscount}
                                            startAdornment={(() => {
                                              if (
                                                packaging.discountType ===
                                                  'manual' ||
                                                !packaging.discountType ||
                                                packaging.discountType ===
                                                  'grosir'
                                              ) {
                                                return (
                                                  <InputAdornment position="start">
                                                    Rp.
                                                  </InputAdornment>
                                                )
                                              }
                                            })()}
                                            endAdornment={(() => {
                                              if (
                                                packaging.discountType ===
                                                'percent'
                                              ) {
                                                return (
                                                  <InputAdornment position="end">
                                                    %
                                                  </InputAdornment>
                                                )
                                              } else if (
                                                packaging.discountType === 'pcs'
                                              ) {
                                                return (
                                                  <InputAdornment position="end">
                                                    pcs
                                                  </InputAdornment>
                                                )
                                              } else if (
                                                packaging.discountType ===
                                                'grosir'
                                              ) {
                                                return (
                                                  <InputAdornment position="end">
                                                    hrg/pc
                                                  </InputAdornment>
                                                )
                                              }
                                            })()}
                                          />
                                        </Grid>
                                        {(packaging.discountType === 'pcs' ||
                                          packaging.discountType ===
                                            'percent' ||
                                          packaging.discountType ===
                                            'grosir') && (
                                          <Grid item>
                                            <Box ml={1}>
                                              <Typography variant="h6">
                                                Rp.{' '}
                                                {(
                                                  packaging.discount || '0'
                                                ).toLocaleString('id-ID')}
                                              </Typography>
                                            </Box>
                                          </Grid>
                                        )}
                                        {(Number(packaging.discount) || 0) >
                                          0.3 *
                                            Number(packaging.unitPrice) *
                                            Number(packaging.qty) && (
                                          <Alert severity="warning">
                                            Diskon lebih besar dari 30%
                                          </Alert>
                                        )}
                                      </Grid>
                                    </Grid>
                                  </React.Fragment>
                                )}
                              </Grid>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      )}
                  </React.Fragment>
                ))}
              </TableBody>
            )}
          </Table>
        </TableContainer>
      </BrowserView>
      <MobileView>
        <TableContainer>
          <TableRow>
            <TableCell colSpan={3}>
              <Box ml={2}>
                <TextField
                  fullWidth
                  placeholder="Cari Kode/Nama Kemasan..."
                  onChange={handleSearch}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>
            </TableCell>
          </TableRow>
          {getPackagingsToShow().map((packaging) => (
            <Box pb={2} pt={2} key={packaging.packagingPartId}>
              <Card
                className={
                  isPackagingSelected(packaging.packagingPartId)
                    ? classes.selectedCard
                    : ''
                }
              >
                <TableContainer>
                  <Table size="small">
                    <TableBody>
                      <React.Fragment>
                        <TableRow>
                          <TableCell>
                            <Box fontWeight="bold" display="inline">
                              Kode Kemasan
                            </Box>
                          </TableCell>
                          <TableCell>{packaging.packagingPartId}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>
                            <Box fontWeight="bold" display="inline">
                              Nama Kemasan
                            </Box>
                          </TableCell>
                          <TableCell>{packaging.partName}</TableCell>
                        </TableRow>
                        {props.showQty && (
                          <TableRow>
                            <TableCell>
                              <Box fontWeight="bold" display="inline">
                                Qty
                              </Box>
                            </TableCell>
                            <TableCell>
                              {props.editableQty ? (
                                <TextField
                                  label="Qty"
                                  type="number"
                                  value={packaging.qty}
                                  onChange={(e) =>
                                    handleQtyChange(
                                      e,
                                      packaging.packagingPartId
                                    )
                                  }
                                  inputProps={{
                                    min: 0,
                                    step: '0.000001',
                                  }}
                                  error={
                                    props.showInventory &&
                                    props.validateStock !== false &&
                                    packaging.qty !== null &&
                                    packaging.qty !== undefined &&
                                    Number(packaging.qty) >
                                      Number(packaging.stock || 0)
                                  }
                                  helperText={
                                    props.showInventory &&
                                    props.validateStock !== false &&
                                    packaging.qty !== null &&
                                    packaging.qty !== undefined &&
                                    Number(packaging.qty) >
                                      Number(packaging.stock || 0)
                                      ? `Melebihi stok (${formatQuantity(
                                          packaging.stock
                                        )})`
                                      : ''
                                  }
                                />
                              ) : (
                                formatQuantity(packaging.qty)
                              )}
                            </TableCell>
                          </TableRow>
                        )}
                        {props.showInventory && (
                          <TableRow>
                            <TableCell>
                              <Box fontWeight="bold" display="inline">
                                Stok
                              </Box>
                            </TableCell>
                            <TableCell>{formatQuantity(packaging.stock)}</TableCell>
                          </TableRow>
                        )}
                        {/* price removed from mobile packaging list view */}
                        {props.showDiscount && (
                          <React.Fragment>
                            <TableRow>
                              <TableCell>
                                <Box fontWeight="bold" display="inline">
                                  Diskon
                                </Box>
                              </TableCell>
                              <TableCell>
                                {`Rp.${new Intl.NumberFormat('id-ID').format(
                                  Number(packaging.discount || 0)
                                )}`}
                                {shouldShowExpand() && (
                                  <IconButton
                                    aria-label="expand row"
                                    size="small"
                                    onClick={(e) => {
                                      e.stopPropagation()
                                      setExpandedRows({
                                        ...expandedRows,
                                        [packaging.packagingPartId]:
                                          !expandedRows[
                                            packaging.packagingPartId
                                          ],
                                      })
                                    }}
                                  >
                                    {expandedRows[packaging.packagingPartId] ? (
                                      <KeyboardArrowUpIcon />
                                    ) : (
                                      <KeyboardArrowDownIcon />
                                    )}
                                  </IconButton>
                                )}
                              </TableCell>
                            </TableRow>
                            {shouldShowExpand() &&
                              expandedRows[packaging.packagingPartId] && (
                                <TableRow className={classes.tableSubRow} hover>
                                  <TableCell colSpan={12}>
                                    <Collapse
                                      in={
                                        expandedRows[packaging.packagingPartId]
                                      }
                                      timeout="auto"
                                      unmountOnExit
                                    >
                                      <Grid container alignItems="flex-end">
                                        {props.editableDiscount && (
                                          <React.Fragment>
                                            {props.editableDiscount && (
                                              <Grid item xs={4}>
                                                <FormControl>
                                                  <InputLabel id="discount-type">
                                                    Jenis Diskon
                                                  </InputLabel>
                                                  <Select
                                                    labelId="discount-type"
                                                    id="discount-type"
                                                    value={
                                                      packaging.discountType ||
                                                      'manual'
                                                    }
                                                    onChange={(e) =>
                                                      handleDiscountTypeChange(
                                                        packaging.packagingPartId,
                                                        String(e.target.value)
                                                      )
                                                    }
                                                  >
                                                    <MenuItem value={'pcs'}>
                                                      Pcs
                                                    </MenuItem>
                                                    <MenuItem value={'manual'}>
                                                      Manual
                                                    </MenuItem>
                                                    <MenuItem value={'percent'}>
                                                      Percent
                                                    </MenuItem>
                                                    <MenuItem value={'grosir'}>
                                                      Grosir
                                                    </MenuItem>
                                                  </Select>
                                                </FormControl>
                                              </Grid>
                                            )}
                                            <Grid item xs={8}>
                                              <InputLabel htmlFor="Diskon">
                                                Diskon
                                              </InputLabel>
                                              <Input
                                                id="Diskon"
                                                autoComplete="off"
                                                type="number"
                                                value={
                                                  packaging.displayDiscount ||
                                                  packaging.discount ||
                                                  ''
                                                }
                                                onChange={(
                                                  e: React.ChangeEvent<HTMLInputElement>
                                                ) =>
                                                  handleDiscountChange(
                                                    packaging.packagingPartId,
                                                    e.target.value.trim()
                                                  )
                                                }
                                                placeholder="0"
                                                onClick={(e) =>
                                                  e.stopPropagation()
                                                }
                                                disabled={
                                                  !props.editableDiscount
                                                }
                                                startAdornment={(() => {
                                                  if (
                                                    packaging.discountType ===
                                                      'manual' ||
                                                    !packaging.discountType ||
                                                    packaging.discountType ===
                                                      'grosir'
                                                  ) {
                                                    return (
                                                      <InputAdornment position="start">
                                                        Rp.
                                                      </InputAdornment>
                                                    )
                                                  }
                                                })()}
                                                endAdornment={(() => {
                                                  if (
                                                    packaging.discountType ===
                                                    'percent'
                                                  ) {
                                                    return (
                                                      <InputAdornment position="end">
                                                        %
                                                      </InputAdornment>
                                                    )
                                                  } else if (
                                                    packaging.discountType ===
                                                    'pcs'
                                                  ) {
                                                    return (
                                                      <InputAdornment position="end">
                                                        pcs
                                                      </InputAdornment>
                                                    )
                                                  } else if (
                                                    packaging.discountType ===
                                                    'grosir'
                                                  ) {
                                                    return (
                                                      <InputAdornment position="end">
                                                        hrg/pc
                                                      </InputAdornment>
                                                    )
                                                  }
                                                })()}
                                              />
                                            </Grid>
                                            {(packaging.discountType ===
                                              'pcs' ||
                                              packaging.discountType ===
                                                'percent' ||
                                              packaging.discountType ===
                                                'grosir') && (
                                              <Grid item xs={12}>
                                                <Typography variant="h6">
                                                  Rp.{' '}
                                                  {(
                                                    packaging.discount || '0'
                                                  ).toLocaleString('id-ID')}
                                                </Typography>
                                              </Grid>
                                            )}
                                            {(Number(packaging.discount) || 0) >
                                              0.3 *
                                                Number(packaging.unitPrice) *
                                                Number(packaging.qty) && (
                                              <Alert severity="warning">
                                                Diskon lebih besar dari 30%
                                              </Alert>
                                            )}
                                          </React.Fragment>
                                        )}
                                      </Grid>
                                    </Collapse>
                                  </TableCell>
                                </TableRow>
                              )}
                          </React.Fragment>
                        )}
                      </React.Fragment>
                    </TableBody>
                  </Table>
                </TableContainer>
              </Card>
            </Box>
          ))}
        </TableContainer>
      </MobileView>
      {getTotalCount() > ROWS_PER_PAGE && (
        <TablePagination
          count={getTotalCount()}
          rowsPerPage={ROWS_PER_PAGE}
          rowsPerPageOptions={[]}
          page={page}
          onPageChange={handleChangePage}
        />
      )}
    </React.Fragment>
  )
}
